// src/components/ReservationModal.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { FaTag, FaCheckCircle, FaTrashAlt } from 'react-icons/fa';

import { createReservation } from '../api/reservation';
import axios from '../api/axios';
import { getBookingContactPrecheck, saveBookingContact } from '../api/userProfile';
import CouponDialog, { type CouponItem as UserCouponItem } from '../components/CouponDialog';
import { useMedicinePrices } from '../hooks/useMedicinePrices';

interface Props {
  selectedSlot: { date: string; time: string };
  service: string;
  visitType: 'first' | 'followup' | string;
  onClose: () => void;
  onSuccess: () => void;
}

type CouponStatus = 'usable' | 'not_started' | 'expired' | 'inactive';

interface ValidateResp {
  ok: boolean;
  code?: string | null;
  link_code?: string | null;
  status?: CouponStatus | null;
  name?: string | null;
  discount_type?: 'percent' | 'amount' | 'fixed_price' | null;
  discount_value?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  message?: string | null;
}

// ── サービス別 簡易価格表（税込） ──
type PriceItem = { name: string; nameZh: string; price: string };
const SERVICE_PRICE_LIST: Record<string, PriceItem[]> = {
  aga_male: [
    { name: 'フィナステリド 1mg/月',     nameZh: '非那雄胺 1mg/月',    price: '¥4,620' },
    { name: 'デュタステリド 0.5mg/月',   nameZh: '度他雄胺 0.5mg/月',  price: '¥7,920' },
    { name: 'ミノキシジル 2.5mg/月',     nameZh: '米诺地尔 2.5mg/月',  price: '¥6,710' },
    { name: 'ミノキシジル 5mg/月',       nameZh: '米诺地尔 5mg/月',    price: '¥9,680' },
  ],
  aga_female: [
    { name: 'スピロノラクトン/月',        nameZh: '螺内酯/月',          price: '¥3,300' },
    { name: 'ミノキシジル/月',           nameZh: '米诺地尔/月',         price: '¥5,500' },
    { name: 'サプリメント/月',           nameZh: '营养补剂/月',          price: '¥2,200' },
  ],
  obesity_male: [
    { name: 'リベルサス 3mg/月',          nameZh: '口服司美格鲁肽 3mg/月', price: '¥6,710' },
    { name: 'リベルサス 7mg/月',          nameZh: '口服司美格鲁肽 7mg/月', price: '¥15,180' },
    { name: 'マンジャロ 2.5mg/月',        nameZh: '替尔泊肽 2.5mg/月',    price: '¥14,300' },
    { name: 'マンジャロ 5mg/月',          nameZh: '替尔泊肽 5mg/月',      price: '¥28,600' },
    { name: 'マンジャロ 7.5mg/月',        nameZh: '替尔泊肽 7.5mg/月',    price: '¥42,900' },
    { name: 'マンジャロ 10mg/月',         nameZh: '替尔泊肽 10mg/月',     price: '¥60,500' },
  ],
  obesity_female: [
    { name: 'リベルサス 3mg/月',          nameZh: '口服司美格鲁肽 3mg/月', price: '¥6,710' },
    { name: 'リベルサス 7mg/月',          nameZh: '口服司美格鲁肽 7mg/月', price: '¥15,180' },
    { name: 'マンジャロ 2.5mg/月',        nameZh: '替尔泊肽 2.5mg/月',    price: '¥14,300' },
    { name: 'マンジャロ 5mg/月',          nameZh: '替尔泊肽 5mg/月',      price: '¥28,600' },
  ],
  ed_premature: [
    { name: 'タダラフィル 2.5mg 連日/月', nameZh: '他达拉非 2.5mg 每日/月', price: '¥4,620' },
    { name: 'タダラフィル 5mg 連日/月',   nameZh: '他达拉非 5mg 每日/月',  price: '¥5,720' },
    { name: 'シルデナフィル 25mg/錠',     nameZh: '西地那非 25mg/片',      price: '¥462' },
    { name: 'シルデナフィル 50mg/錠',     nameZh: '西地那非 50mg/片',      price: '¥792' },
  ],
  pill: [
    { name: 'トリキュラー 1シート(28日)', nameZh: '特洁乐 1版(28天)',      price: '¥3,520' },
    { name: 'マーベロン 1シート(28日)',   nameZh: '妈富隆 1版(28天)',      price: '¥3,520' },
  ],
  after_pill: [
    { name: 'アフターピル 1回分',         nameZh: '紧急避孕药 1次',        price: '¥8,800' },
  ],
  // ★ LP/詳細ページ(/menu/medical-skincare)と同じ「内服補剤」構成に統一(以前は外用薬でズレていた)。
  //   税込表示。DB に skincare データが入れば DB 側が優先される。
  skincare: [
    { name: '3種セット(肌ケアライト)',      nameZh: '3种套餐(护肤轻盈款)',   price: '¥6,050' },
    { name: '4種セット(肌ケアベーシック)',  nameZh: '4种套餐(护肤基础款)',   price: '¥7,920' },
    { name: 'トラネキサム酸 250mg',         nameZh: '氨甲环酸 250mg',        price: '¥3,300' },
    { name: 'ユベラ 50mg',                  nameZh: '维生素E 50mg',          price: '¥2,200' },
    { name: 'シナール 1000mg',              nameZh: '希那尔 1000mg',         price: '¥3,300' },
    { name: 'L-システイン 80mg',            nameZh: 'L-半胱氨酸 80mg',       price: '¥2,530' },
    { name: '十味敗毒湯 250mg',             nameZh: '十味败毒汤 250mg',      price: '¥6,600' },
  ],
};
// 一般配送: ¥500、コールドチェーン（肥満症注射薬）: ¥2,000
const SHIPPING_FEE: Record<string, number> = {
  aga_male:       500,
  aga_female:     500,
  obesity_male:   2000,  // マンジャロ等の注射薬はコールドチェーン
  obesity_female: 2000,
  ed_premature:   500,
  pill:           500,
  after_pill:     500,
  skincare:       500,
};

// 予約モーダルの料金表も LP/詳細ページと同じく DB を唯一のソースに統一する(サービス別に取得)。
// after_pill は DB に専用分類が無いため 'pill' に寄せる。DB に該当が無ければ SERVICE_PRICE_LIST にフォールバック。

function PriceEstimate({ service, coupon, employeeDiscount }: {
  service: string;
  coupon: UserCouponItem | null;
  employeeDiscount?: { rate: number; visit_type: string } | null;
}) {
  const { t, i18n } = useTranslation();
  const [showPriceList, setShowPriceList] = React.useState(false);
  const isZh = i18n.language?.startsWith('zh');

  const shippingFee = SHIPPING_FEE[service] ?? 500;

  // 料金表は DB(admin設定)優先。表示は単次購入(single_purchase)の税込のみ(定期便は非表示)。
  // DB に該当サービスの薬が無ければハードコードの SERVICE_PRICE_LIST にフォールバック。
  const dbServiceType = service === 'after_pill' ? 'pill' : service;
  const { medicines } = useMedicinePrices(dbServiceType, isZh ? 'zh' : 'ja');
  const dbPriceList = medicines
    .map((m) => {
      const sp = m.price_plans.find((p) => p.plan_type === 'single_purchase');
      return sp ? { name: m.name, nameZh: m.name, price: `¥${sp.price_tax_included.toLocaleString('ja-JP')}` } : null;
    })
    .filter((x): x is PriceItem => !!x);
  const priceList: PriceItem[] = dbPriceList.length > 0 ? dbPriceList : (SERVICE_PRICE_LIST[service] ?? []);

  let discountNote: string | null = null;
  if (coupon && coupon.discount_value != null) {
    const val = Number(coupon.discount_value);
    if (coupon.discount_type === 'percent')          discountNote = `${val}% OFF`;
    else if (coupon.discount_type === 'amount')      discountNote = `¥${val.toLocaleString()} OFF`;
    else if (coupon.discount_type === 'fixed_price') discountNote = `¥${val.toLocaleString()} に固定`;
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-4 text-sm">
      <p className="text-xs font-bold text-gray-600 mb-2">
        {t('reserve.price_estimate_title') || 'お支払いについて'}
      </p>
      <div className="space-y-1.5 text-gray-700">
        <div className="flex justify-between">
          <span>{t('reserve.price_consultation') || '診療費'}</span>
          <span className="font-semibold text-green-600">0 円</span>
        </div>

        {discountNote && (
          <div className="flex justify-between text-coral font-medium">
            <span>{t('reserve.price_coupon') || 'クーポン割引'}</span>
            <span>- {discountNote}</span>
          </div>
        )}

        {!discountNote && employeeDiscount && (
          <div className="flex justify-between items-center text-blue-600 font-medium">
            <span className="flex items-center gap-1">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-700 text-amber-50 font-semibold">
                {employeeDiscount.visit_type === 'first' ? '初回' : '継続'}割引
              </span>
              <span className="text-xs text-gray-500">支払い時に自動適用</span>
            </span>
            <span>{employeeDiscount.rate}% OFF</span>
          </div>
        )}

        <div className="flex justify-between">
          <span>{t('reserve.price_shipping') || '送料'}</span>
          <span>¥{shippingFee.toLocaleString()}</span>
        </div>

        <div className="flex justify-between items-center border-t border-gray-200 pt-1.5 mt-1">
          <span className="text-gray-500">{t('reserve.price_medicine') || '処方薬代'}</span>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>{t('reserve.price_medicine_after') || '診療後確定'}</span>
            {priceList.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPriceList(v => !v)}
                className="text-coral underline hover:opacity-80"
              >
                {showPriceList
                  ? (isZh ? '收起 ▲' : '閉じる ▲')
                  : (t('reserve.price_see_detail') || '料金表 ▼')}
              </button>
            )}
          </div>
        </div>

        {/* 簡易価格表ポップアップ */}
        {showPriceList && priceList.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 mt-1 space-y-1">
            {priceList.map((item, i) => (
              <div key={i} className="flex justify-between text-xs text-gray-600">
                <span>{isZh ? item.nameZh : item.name}</span>
                <span className="font-medium text-gray-800">{item.price}</span>
              </div>
            ))}
            <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-100">
              {isZh ? '※ 含税价格。实际金额以医生的处方为准。' : '※ 税込。医師の処方により異なります。'}
            </p>
          </div>
        )}
      </div>
      <p className="text-[10px] text-gray-400 mt-2">
        {t('reserve.price_disclaimer') || '※ 請求は診療後に確定します。クーポンは次の画面で適用されます。'}
      </p>
    </div>
  );
}

// 訪問区分の文字ゆらぎを吸収し 'first' | 'followup' に正規化
function normalizeVisitType(input: string | undefined | null): 'first' | 'followup' {
  const v = String(input || '').trim().toLowerCase();
  const FIRST = new Set(['first', 'first_visit', 'initial', 'new', 'firsttime', '初診', '初诊', 'shoshin', 'chuzhen']);
  const FOLLOWUP = new Set(['followup', 'follow_up', 'revisit', 'return', 'repeat', '再診', '复诊', 'saishin', 'fuzhen']);
  if (FOLLOWUP.has(v)) return 'followup';
  if (FIRST.has(v)) return 'first';
  return 'first';
}

// UI 用：日付の整形
const fmtDateForUI = (dateISO: string, lang: string, weekdayText: string) => {
  const d = dayjs(dateISO);
  if (lang === 'zh') return `${d.format('M月D日')}（${weekdayText}）`;
  return `${d.format('M月D日')}（${weekdayText}）`;
};

// UI 用：コードの末尾4桁マスク（表示は大文字）
const maskCode = (raw?: string) => {
  const up = (raw || '').toUpperCase();
  if (up.length <= 4) return '****';
  return up.slice(0, -4) + '****';
};

const ReservationModal: React.FC<Props> = ({
  selectedSlot,
  service,
  visitType,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // 認証トークン（未ログイン時はログインへ誘導）
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // 認証済みユーザー向けステート
  const [checked, setChecked] = useState(false);
  const [attendanceAck, setAttendanceAck] = useState(false);
  const [showSecondConfirm, setShowSecondConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [needsTranslation, setNeedsTranslation] = useState<boolean | null>(null);
  // 連絡先(電話)確認 — precheck で「電話が必要か / 氏名も必要か」を判定し、最終確認画面で入力を求める(必須)
  const [needsPhone, setNeedsPhone] = useState(false);   // 連絡可能な電話が未登録
  const [needsName, setNeedsName] = useState(false);     // profile 未作成 → 氏名も必要
  const [phoneInput, setPhoneInput] = useState('');
  const [seiInput, setSeiInput] = useState('');   // 姓
  const [meiInput, setMeiInput] = useState('');   // 名
  const [phoneSkipped, setPhoneSkipped] = useState(false); // 逃生口:「電話をお持ちでない方」
  const [showSkipWarn, setShowSkipWarn] = useState(false);

  // クーポン入力・選択のステート
  const [couponInput, setCouponInput] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<UserCouponItem | null>(null);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);

  // 言語依存：曜日と表示テキスト
  const normalizedVisitType = useMemo(() => normalizeVisitType(visitType), [visitType]);
  const weekdayText = useMemo(() => {
    const d = dayjs(selectedSlot.date);
    if (i18n.language === 'zh') {
      const zhWeek = ['日', '一', '二', '三', '四', '五', '六'];
      return `周${zhWeek[d.day()]}`;
    }
    const jaWeek = ['日', '月', '火', '水', '木', '金', '土'];
    return jaWeek[d.day()];
  }, [i18n.language, selectedSlot.date]);
  const loginMethod =
    typeof window !== 'undefined' ? localStorage.getItem('login_method') : null;
  const shouldAskTranslation =
    String(i18n.language).toLowerCase().startsWith('zh') && loginMethod !== 'line';

  // 日時の一行表示
  const renderDateTime = () => {
    return `${fmtDateForUI(
      selectedSlot.date,
      i18n.language,
      weekdayText,
    )} ${i18n.language === 'zh' ? '' : ' '}${selectedSlot.time}${i18n.language === 'zh' ? '' : ' 開始'
      }`;
  };

  // 社員割引情報
  const [employeeDiscount, setEmployeeDiscount] = useState<{ rate: number; visit_type: string } | null>(null);

  // ダイアログを開いたタイミングで保有クーポンを軽くプリフェッチ（使えるものが1件なら事前選択）+ 社員割引確認
  useEffect(() => {
    const prefetch = async () => {
      try {
        const res = await axios.get<UserCouponItem[]>('/user/coupons');
        const usable = (res.data || []).filter((x) => x.status === 'usable');
        if (usable.length === 1) {
          setSelectedCoupon(usable[0]);
        }
      } catch {
        // 失敗しても UX に影響させない
      }
    };
    prefetch();

    // 社員割引チェック
    const checkEmployeeDiscount = async () => {
      try {
        const { data } = await axios.get('/user/company');
        if (data?.coupons?.length) {
          const targetType = data.is_first_visit ? 'first' : 'followup';
          const match = data.coupons.find((c: any) => c.coupon_type === targetType)
            ?? data.coupons[0];
          if (match) {
            setEmployeeDiscount({
              rate: match.discount_value,
              visit_type: data.is_first_visit ? 'first' : 'followup',
            });
          }
        }
      } catch { /* ignore */ }
    };
    checkEmployeeDiscount();

    // 連絡先(電話)の precheck — 未登録なら最終確認で入力を求める(氏名も必要かを含む)
    const checkContact = async () => {
      try {
        const pc = await getBookingContactPrecheck();
        setNeedsPhone(!!pc.needs_phone);
        setNeedsName(!!pc.needs_name);
      } catch { /* 取得失敗時は追加入力を求めない(予約を妨げない) */ }
    };
    checkContact();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 手入力→検証→OK ならバインド→選択（只允许 usable）
  const applyCouponByInput = async () => {
    const raw = (couponInput || '').trim().toUpperCase();
    if (!raw) {
      setCouponMsg(t('coupons.enter_code') || 'クーポンコードを入力してください');
      return;
    }
    setCouponBusy(true);
    setCouponMsg(null);
    try {
      // 1) 検証
      const vres = await axios.post<ValidateResp>('/user/coupons/validate', {
        code: raw,
      });
      const v = vres.data;
      if (!v.ok) {
        setCouponMsg(
          v.message ||
          t('coupons.validate_failed') ||
          'このコードは割引に使用できません。',
        );
        return;
      }
      if (!v.discount_type || v.discount_value == null) {
        setCouponMsg(
          t('coupons.referral_only') ||
          '紹介リンク専用のコードです（割引は適用されません）。',
        );
        return;
      }
      // 只接受 status = usable 的券
      if (v.status && v.status !== 'usable') {
        setCouponMsg(
          t('coupons.not_usable') ||
          'このクーポンは現在ご利用いただけません。（期限切れ/未開始/無効）',
        );
        return;
      }

      // 2) バインド（冪等）→ バインド後のデータを選択状態に採用
      const bind = await axios.post<UserCouponItem>('/user/coupons/bind', {
        code: raw,
      });
      setSelectedCoupon(bind.data);
      setCouponMsg(t('coupons.applied_success') || 'クーポンを適用しました。');
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        t('common.error') ||
        'エラーが発生しました。';
      setCouponMsg(String(msg));
    } finally {
      setCouponBusy(false);
    }
  };

  // 選択解除（ここでは「使用」していないため、解除は単に UI から外すだけ）
  const clearSelectedCoupon = () => {
    setSelectedCoupon(null);
    setCouponMsg(null);
  };

  // 最終確認の確定（クーポンはこの時点ではまだ「使用」しない）
  const handleFinalConfirm = async () => {
    if (!token) {
      const redirect = `${location.pathname}${location.search}`;
      navigate(`/login?redirectTo=${encodeURIComponent(redirect)}`);
      return;
    }
    if (submitting) return;

    try {
      setSubmitting(true);
      setError(null);

      // 連絡先(電話)が必要で逃生口を選んでいない場合は保存(必須)。
      // 診察前に連絡できるようにするのが目的のため、保存できなければ予約を中断する(逃生口は利用可能)。
      if (needsPhone && !phoneSkipped) {
        const _pdigits = phoneInput.replace(/\D/g, '');
        if (!/^\d{10,13}$/.test(_pdigits)) {
          setError((t('reserve.phone_invalid') as string) || '電話番号（半角数字10〜13桁）を入力してください。');
          return; // finally で submitting 解除
        }
        if (needsName && (!seiInput.trim() || !meiInput.trim())) {
          setError((t('reserve.name_required') as string) || 'お名前(姓・名)をご入力ください。');
          return;
        }
        try {
          // 全站と同じ E.164(+81)形式で保存。保存先は profile.phone_number のみ。
          // 先頭の0を除去し、既に国番号(81)付きなら二重に付与しない(例: 819012… → +819012…)。
          const _local = _pdigits.replace(/^0/, '');
          const _e164 = _local.startsWith('81') ? '+' + _local : '+81' + _local;
          await saveBookingContact({
            phone_number: _e164,
            ...(needsName ? { full_name: `${seiInput.trim()} ${meiInput.trim()}`.trim() } : {}),
          });
        } catch (e: any) {
          const code = e?.response?.data?.detail?.code;
          if (code === 'NAME_REQUIRED') {
            // profile 未作成で氏名が無い → 氏名欄を出して再入力を促す
            setNeedsName(true);
            setError((t('reserve.name_required') as string) || 'お名前をご入力ください。');
          } else {
            setError((t('reserve.phone_save_failed') as string) || '電話番号の保存に失敗しました。もう一度お試しください。');
          }
          return; // 電話が必須のため、保存できなければ予約を中断する
        }
      }

      const payload: any = {
        service,
        visit_type: normalizedVisitType,
        date: selectedSlot.date,
        time: selectedSlot.time,
        status: 'pending',
        needs_translation: needsTranslation === true,
      };
      // 此处不附带 user_coupon_id，实际扣款在支付流程前一页处理

      await createReservation(payload);

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.detail ||
        err?.message;
      setError(detail || (t('reserve.error_generic') as string));
      console.error('予約作成エラー:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // 未ログインのとき：ログイン誘导
  if (!token) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">
            {t('reserve.login_required')}
          </h2>
          <p className="text-center text-sm text-gray-600 mb-4">
            {t('reserve.please_login_to_continue')}
          </p>
          <div className="flex justify-center">
            <a
              href={`/login?redirectTo=${encodeURIComponent(
                `${location.pathname}${location.search}`,
              )}`}
              className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap text-sm"
            >
              {t('reserve.go_to_login')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // 完了表示
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 text-center">
          <h2 className="text-lg font-bold text-green-700 mb-4">
            {t('reserve.success')}
          </h2>
          <p className="text-sm text-gray-700 mb-2">
            {t('reserve.completed')}
          </p>
          <p className="text-xs text-gray-500">
            {t('reserve.redirecting_in_5s')}
          </p>
        </div>
      </div>
    );
  }

  // 連絡先入力の妥当性(電話は必須。氏名が必要なら氏名も)。逃生口を選べば OK。
  const _phoneDigits = phoneInput.replace(/\D/g, '');
  const _phoneValid = /^\d{10,13}$/.test(_phoneDigits);
  const _nameValid = !needsName || (!!seiInput.trim() && !!meiInput.trim());
  const contactOk = !needsPhone || phoneSkipped || (_phoneValid && _nameValid);

  // 二段階目の最終確認
  if (showSecondConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-8 py-10 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('reserve.final_confirm')}
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            {t('reserve.please_double_check')}
          </p>

          <div className="bg-coral/5 border border-coral/20 rounded-lg p-4 mb-6">
            <p className="text-xl font-semibold text-navy">
              {renderDateTime()}
            </p>
          </div>

          {/* 診療時間厳守・宽限内の入室+問診票完了リマインド */}
          <div className="mb-5 text-left bg-yellow-50 border border-yellow-300 rounded p-3">
            <p className="text-sm text-yellow-900">
              {t('reserve.attendance_reminder_title') || '診察時間になりましたら、時間通りにご入室ください。'}
            </p>
            <p className="text-xs text-yellow-800 mt-1">
              {t('reserve.attendance_reminder_body') ||
                '診察時間から15分以内に入室と問診票の記入を完了していない場合は、キャンセルとさせていただきます。'}
            </p>
            <label className="flex items-start text-sm text-gray-700 mt-3">
              <input
                type="checkbox"
                checked={attendanceAck}
                onChange={() => setAttendanceAck((v) => !v)}
                className="mt-1 mr-2"
              />
              <span>{t('reserve.attendance_reminder_checkbox') || '上記内容を確認しました'}</span>
            </label>
          </div>

          {selectedCoupon && (
            <div className="mb-4 text-sm text-left bg-green-50 border border-green-200 rounded p-3">
              <div className="flex items-center justify-between">
                <span className="text-green-800">
                  {t('reserve.coupon_selected') || 'クーポンを選択しました'}
                  ：<b>{maskCode(selectedCoupon.code)}</b>
                </span>
                <button
                  type="button"
                  onClick={clearSelectedCoupon}
                  className="text-red-600 hover:text-red-700 flex items-center gap-1 text-xs"
                >
                  <FaTrashAlt /> {t('common.remove') || '削除'}
                </button>
              </div>
              <div className="text-green-700 mt-1 text-xs">
                {t('reserve.coupon_will_apply_later') ||
                  '※ 割引の適用は決済前の最終確認画面で行います。'}
              </div>
            </div>
          )}

          {shouldAskTranslation && (
            <div className="mb-5 text-left bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                {t('reserve.translation_required_question') ||
                  '请确认：本次线上诊疗是否需要中文翻译服务？'}
              </p>
              <p className="text-xs text-blue-700 mb-3">
                {t('reserve.translation_required_hint') ||
                  '此信息会直接影响医生与翻译人员排班。提交后如有变更，请尽快联系客服处理。'}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setNeedsTranslation(true)}
                  className={classNames(
                    'px-3 py-1.5 rounded-md text-sm border transition',
                    needsTranslation === true
                      ? 'bg-coral text-white border-coral'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-coral/60',
                  )}
                >
                  {t('reserve.translation_required_yes') || '需要中文翻译'}
                </button>
                <button
                  type="button"
                  onClick={() => setNeedsTranslation(false)}
                  className={classNames(
                    'px-3 py-1.5 rounded-md text-sm border transition',
                    needsTranslation === false
                      ? 'bg-coral text-white border-coral'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-coral/60',
                  )}
                >
                  {t('reserve.translation_required_no') || '不需要翻译'}
                </button>
              </div>
              {needsTranslation === null && (
                <p className="text-xs text-red-600 mt-2">
                  {t('reserve.translation_required_error') ||
                    '请先选择是否需要中文翻译服务。'}
                </p>
              )}
            </div>
          )}

          {/* 連絡先(電話)入力 — 診察前に連絡できるよう必須。逃生口あり */}
          {needsPhone && !phoneSkipped && !showSkipWarn && (
            <div className="mb-5 text-left bg-coral/5 border border-coral/20 rounded p-3">
              <p className="text-sm font-semibold text-gray-800 mb-1">
                {t('reserve.contact_required_title') || 'より良いサービスのために'}
              </p>
              <p className="text-xs text-gray-500 mb-3">
                {t('reserve.contact_required_desc') || 'ご予約前に、ご連絡先のご登録をお願いいたします。'}
              </p>
              {needsName && (
                <div className="mb-2">
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('reserve.name_field_label') || 'お名前（必須）'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={seiInput}
                      onChange={(e) => setSeiInput(e.target.value)}
                      placeholder={t('reserve.sei_placeholder') || '姓（例: 山田）'}
                      className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-coral/30 text-sm"
                    />
                    <input
                      type="text"
                      value={meiInput}
                      onChange={(e) => setMeiInput(e.target.value)}
                      placeholder={t('reserve.mei_placeholder') || '名（例: 太郎）'}
                      className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-coral/30 text-sm"
                    />
                  </div>
                </div>
              )}
              <label className="block text-xs text-gray-600 mb-1">
                {t('reserve.phone_field_label_required') || '電話番号（必須）'}
              </label>
              <input
                type="tel"
                inputMode="numeric"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder={t('reserve.phone_placeholder') || '電話番号（半角数字）'}
                className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-coral/30 font-mono text-sm"
              />
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setShowSkipWarn(true)}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  {t('reserve.phone_no_phone_link') || '電話をお持ちでない方はこちら'}
                </button>
              </div>
            </div>
          )}

          {/* 逃生口の警告 */}
          {needsPhone && showSkipWarn && !phoneSkipped && (
            <div className="mb-5 text-left bg-yellow-50 border border-yellow-300 rounded p-3">
              <p className="text-sm text-yellow-900 mb-3">
                {t('reserve.phone_skip_warn') ||
                  '電話番号のご登録がない場合、診察前にクリニックからご連絡できないことがあります。それでもよろしいですか？'}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowSkipWarn(false)}
                  className="flex-1 px-3 py-1.5 rounded-md text-sm bg-white border border-gray-300 hover:bg-gray-50"
                >
                  {t('reserve.phone_skip_back') || '戻って入力'}
                </button>
                <button
                  type="button"
                  onClick={() => { setPhoneSkipped(true); setShowSkipWarn(false); }}
                  className="flex-1 px-3 py-1.5 rounded-md text-sm bg-yellow-500 text-white hover:opacity-90"
                >
                  {t('reserve.phone_skip_confirm') || '電話なしで予約する'}
                </button>
              </div>
            </div>
          )}

          {/* 逃生口を選んだ後の表示 */}
          {needsPhone && phoneSkipped && (
            <div className="mb-5 text-left bg-gray-50 border border-gray-200 rounded p-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {t('reserve.phone_skipped_note') || '電話番号なしで予約します'}
              </span>
              <button
                type="button"
                onClick={() => setPhoneSkipped(false)}
                className="text-xs text-coral hover:underline"
              >
                {t('reserve.phone_skipped_undo') || '入力する'}
              </button>
            </div>
          )}

          {error && (
            <p className="mb-4 text-red-600 font-semibold text-sm">
              {error}
            </p>
          )}

          <div className="flex justify-between mt-8 gap-4">
            <button
              onClick={() => setShowSecondConfirm(false)}
              className="flex-1 px-4 py-2 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 text-sm transition"
            >
              {t('reserve.back')}
            </button>
            <button
              onClick={handleFinalConfirm}
              disabled={submitting || (shouldAskTranslation && needsTranslation === null) || !contactOk || !attendanceAck}
              className={classNames(
                'flex-1 px-4 py-2 rounded-lg shadow-md text-sm font-medium transition',
                submitting || (shouldAskTranslation && needsTranslation === null) || !contactOk || !attendanceAck
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-coral text-white hover:opacity-90',
              )}
            >
              {submitting
                ? (t('reserve.submitting') as string) || '処理中…'
                : (t('reserve.confirm') as string)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 初回確認
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-6 py-8">
        <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">
          {t('reserve.confirm_title')}
        </h2>

        <div className="text-center text-sm text-gray-700 font-semibold mb-1">
          {t(`reserve.services.${service}`, service)}
        </div>
        <p className="text-center text-sm text-gray-600 mb-4">
          {renderDateTime()}
        </p>

        {/* 注意事項 */}
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm rounded p-3 mb-4">
          <strong>{t('reserve.notice_title')}</strong>
          <ul className="mt-1 list-disc list-inside">
            <li>{t('reserve.notice_1')}</li>
            <li>{t('reserve.notice_2')}</li>
            <li>{t('reserve.notice_3')}</li>
            <li>{t('reserve.notice_4')}</li>
          </ul>
        </div>

        {/* クーポン：バインド/選択のみ。適用は決済画面側 */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">
            {t('auth.coupon_label') || 'クーポン（任意）'}
          </label>

          {selectedCoupon ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2">
              <div className="text-sm text-green-800 flex items-center gap-2">
                <FaCheckCircle />
                <span>
                  <b>
                    {t('reserve.coupon_selected') || 'クーポンを選択しました'}
                  </b>
                  ：<b>{maskCode(selectedCoupon.code)}</b>
                </span>
              </div>
              <button
                type="button"
                onClick={clearSelectedCoupon}
                className="text-red-600 hover:text-red-700 flex items-center gap-1 text-xs"
              >
                <FaTrashAlt /> {t('common.remove') || '削除'}
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center rounded-lg px-3 py-2 border border-gray-300 focus-within:ring-2 focus-within:ring-coral">
                  <FaTag className="text-gray-400 mr-2" />
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    onCompositionEnd={(e) =>
                      setCouponInput(e.currentTarget.value.toUpperCase())
                    }
                    onBlur={(e) =>
                      setCouponInput(e.target.value.toUpperCase())
                    }
                    placeholder={
                      t('auth.coupon_placeholder') ||
                      'お持ちの方はご入力ください'
                    }
                    className="w-full outline-none text-gray-700"
                    autoCapitalize="none"
                    autoComplete="off"
                    spellCheck={false}
                    autoCorrect="off"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyCouponByInput}
                  disabled={couponBusy || !couponInput.trim()}
                  className={classNames(
                    'px-4 py-2 rounded-lg text-white',
                    couponBusy || !couponInput.trim()
                      ? 'bg-coral/80 opacity-60 cursor-not-allowed'
                      : 'bg-coral hover:opacity-90',
                  )}
                >
                  {couponBusy
                    ? t('auth.sending') || '送信中…'
                    : t('common.apply') || '適用'}
                </button>
              </div>
              {couponMsg && (
                <p className="text-xs mt-2 text-gray-700">{couponMsg}</p>
              )}

              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setCouponDialogOpen(true)}
                  className="text-xs text-coral hover:underline"
                >
                  {t('reserve.pick_from_my_coupons') || 'マイクーポンから選ぶ'}
                </button>
              </div>
            </>
          )}

          <p className="text-xs text-gray-500 mt-2">
            {t('reserve.coupon_will_apply_later') ||
              '※ 割引の適用は決済前の最終確認画面で行います。'}
          </p>
        </div>

        {/* ⑧ 料金目安サマリー（診療前に費用感を提示） */}
        <PriceEstimate service={service} coupon={selectedCoupon} employeeDiscount={employeeDiscount} />

        {/* 同意チェック */}
        <label className="flex items-start text-sm text-gray-700 mb-4">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => setChecked((v) => !v)}
            className="mt-1 mr-2"
          />
          <span>{t('reserve.confirm_checkbox')}</span>
        </label>

        {error && (
          <p className="mb-4 text-red-600 font-semibold text-sm text-center">
            {error}
          </p>
        )}

        <div className="flex justify-between mt-4">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm disabled:opacity-60"
          >
            {t('reserve.cancel')}
          </button>
          <button
            onClick={() => setShowSecondConfirm(true)}
            disabled={!checked}
            className={classNames(
              'px-4 py-2 rounded text-sm transition-colors duration-200',
              !checked
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-coral text-white hover:opacity-90',
            )}
          >
            {t('reserve.confirm')}
          </button>
        </div>
      </div>

      {/* マイクーポン一覧ダイアログ：只显示可用券，点击即选择 */}
      <CouponDialog
        open={couponDialogOpen}
        onClose={() => setCouponDialogOpen(false)}
        onPick={(c) => {
          setSelectedCoupon(c);
          setCouponDialogOpen(false);
        }}
        pickUsableOnly
      />
    </div>
  );
};

export default ReservationModal;
